import { supabase } from '@/integrations/supabase/client';
import { cleanupExportsWithRetry } from './exportCleanupService';
import { retryFailedChunks } from './retryFailedChunks';

/**
 * Service to manage export monitoring and cleanup
 */
export class ExportMonitoringService {
  private static instance: ExportMonitoringService;
  private cleanupInterval: NodeJS.Timeout | null = null;
  private retryInterval: NodeJS.Timeout | null = null;
  private readonly CLEANUP_INTERVAL = 5 * 60 * 1000; // 5 minutes
  private readonly RETRY_INTERVAL = 2 * 60 * 1000; // 2 minutes for faster retry response
  private readonly MAX_EXPORT_AGE = 30 * 60 * 1000; // 30 minutes

  private constructor() {}

  static getInstance(): ExportMonitoringService {
    if (!ExportMonitoringService.instance) {
      ExportMonitoringService.instance = new ExportMonitoringService();
    }
    return ExportMonitoringService.instance;
  }

  /**
   * Start automatic cleanup of stuck exports
   */
  startAutomaticCleanup(): void {
    if (this.cleanupInterval) {
      return; // Already running
    }

    console.log('Starting automatic export cleanup and retry service');
    
    // Run initial cleanup
    this.runCleanup();
    
    // Run initial retry check
    this.runRetryCheck();
    
    // Schedule periodic cleanup
    this.cleanupInterval = setInterval(() => {
      this.runCleanup();
    }, this.CLEANUP_INTERVAL);
    
    // Schedule more frequent retry checks
    this.retryInterval = setInterval(() => {
      this.runRetryCheck();
    }, this.RETRY_INTERVAL);
  }

  /**
   * Stop automatic cleanup
   */
  stopAutomaticCleanup(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    if (this.retryInterval) {
      clearInterval(this.retryInterval);
      this.retryInterval = null;
    }
    console.log('Stopped automatic export cleanup and retry service');
  }

  /**
   * Run cleanup process
   */
  private async runCleanup(): Promise<void> {
    try {
      const result = await cleanupExportsWithRetry();
      if (result.cleaned > 0) {
        console.log(`Cleanup: ${result.message}`);
      }
    } catch (error) {
      console.error('Error during automatic export cleanup:', error);
    }
  }

  /**
   * Run retry check for failed chunks
   */
  private async runRetryCheck(): Promise<void> {
    try {
      await retryFailedChunks();
      console.log('Retry check completed for failed chunks');
    } catch (error) {
      console.error('Error during automatic retry check:', error);
    }
  }

  /**
   * Get export statistics for monitoring
   */
  async getExportStats(): Promise<{
    total: number;
    queued: number;
    processing: number;
    completed: number;
    failed: number;
    stuck: number;
  }> {
    try {
      const { data: exports, error } = await supabase
        .from('csv_exports')
        .select('status, created_at, started_at')
        .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()); // Last 24 hours

      if (error) {
        throw error;
      }

      const stats = {
        total: exports?.length || 0,
        queued: 0,
        processing: 0,
        completed: 0,
        failed: 0,
        stuck: 0
      };

      const now = Date.now();
      exports?.forEach(exp => {
        stats[exp.status as keyof typeof stats]++;
        
        // Check if stuck (processing for more than max age)
        if (exp.status === 'processing' && exp.started_at) {
          const startTime = new Date(exp.started_at).getTime();
          if (now - startTime > this.MAX_EXPORT_AGE) {
            stats.stuck++;
          }
        }
      });

      return stats;
    } catch (error) {
      console.error('Error getting export stats:', error);
      return {
        total: 0,
        queued: 0,
        processing: 0,
        completed: 0,
        failed: 0,
        stuck: 0
      };
    }
  }

  /**
   * Force cleanup of all processing exports (emergency use)
   */
  async emergencyCleanup(): Promise<{ success: boolean; cleanedCount: number; error?: string }> {
    try {
      console.log('Running emergency cleanup of all processing exports');
      
      const { data: processingExports, error: findError } = await supabase
        .from('csv_exports')
        .select('export_id')
        .in('status', ['queued', 'processing']);

      if (findError) {
        return { success: false, cleanedCount: 0, error: findError.message };
      }

      if (!processingExports || processingExports.length === 0) {
        return { success: true, cleanedCount: 0 };
      }

      const { error: updateError } = await supabase
        .from('csv_exports')
        .update({
          status: 'failed',
          error_message: 'Export cancelled due to system maintenance. Please retry.',
          completed_at: new Date().toISOString(),
          failure_step: 'EMERGENCY_CLEANUP'
        })
        .in('status', ['queued', 'processing']);

      if (updateError) {
        return { success: false, cleanedCount: 0, error: updateError.message };
      }

      console.log(`Emergency cleanup completed: ${processingExports.length} exports cancelled`);
      return { success: true, cleanedCount: processingExports.length };
    } catch (error) {
      return { 
        success: false, 
        cleanedCount: 0, 
        error: error instanceof Error ? error.message : 'Unknown error' 
      };
    }
  }
}

// Create and start the service
export const exportMonitoringService = ExportMonitoringService.getInstance();

